import { useState, useCallback, useMemo } from 'react';
import { SGLOrder, OrderComparison, ComparisonMetrics } from '../types/comparative';
import { EcommerceOrder } from '../types/ecommerce';
import { ComparativeService } from '../services/comparativeService';
import { EcommerceService } from '../services/ecommerceService';

export const useStoreComparison = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<OrderComparison[]>([]);
  const [metrics, setMetrics] = useState<ComparisonMetrics | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 50
  });

  /**
   * Función auxiliar para normalizar IDs y hacer comparaciones más robustas
   */
  const normalizeId = useCallback((id: string | number | undefined): string => {
    if (id === undefined || id === null) return '';
    return id.toString().trim();
  }, []);

  /**
   * Función auxiliar para comparar IDs con diferentes tipos de datos
   */
  const compareIds = useCallback((id1: string | number | undefined, id2: string | number | undefined): boolean => {
    const normalized1 = normalizeId(id1);
    const normalized2 = normalizeId(id2);
    
    // Comparación directa
    if (normalized1 === normalized2) return true;
    
    // Comparación numérica si ambos son números válidos
    const num1 = parseInt(normalized1);
    const num2 = parseInt(normalized2);
    if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;
    
    return false;
  }, [normalizeId]);

  /**
   * Comparar detalles específicos de los pedidos
   * Solo verifica si el pedido existe en SGL o no
   */
  const compareOrderDetails = useCallback((sourceOrder: EcommerceOrder | SGLOrder, targetOrder?: SGLOrder | EcommerceOrder, matchType?: 'orders_id' | 'origin_order_id' | 'id' | 'total_date' | 'none', store?: string) => {
    if (!targetOrder) {
      const message = store === 'OFIMA' ? 'Pedido no existe en OFIMA' : 'Pedido no existe en SGL';
      return {
        exists: false,
        statusMatch: false,
        totalMatch: false,
        dateMatch: false,
        discrepancies: [message],
        syncStatus: 'missing' as 'complete' | 'missing' | 'discrepancy',
        matchType: matchType || 'none'
      };
    }

    // Si existe, es completo (no importan las diferencias)
    return {
      exists: true,
      statusMatch: true,
      totalMatch: true,
      dateMatch: true,
      discrepancies: [],
      syncStatus: 'complete' as 'complete' | 'missing' | 'discrepancy',
      matchType: matchType || 'none'
    };
  }, []);

  const calculateComparisonMetrics = useCallback((comparisons: OrderComparison[], sglTotal: number, ecommerceTotal: number): ComparisonMetrics => {
    const synchronized = comparisons.filter(c => c.comparison.syncStatus === 'complete').length;
    const missing = comparisons.filter(c => c.comparison.syncStatus === 'missing').length;
    const discrepancies = 0; // Ya no hay discrepancias, solo existe o no existe
    
    const syncPercentage = ecommerceTotal > 0 ? (synchronized / ecommerceTotal) * 100 : 0;

    return {
      totalEcommerceOrders: ecommerceTotal,
      totalSGLOrders: sglTotal,
      synchronizedOrders: synchronized,
      missingInSGL: missing,
      discrepancies,
      syncPercentage: Math.round(syncPercentage * 100) / 100
    };
  }, []);

  /**
   * Realizar comparación específica para OFIMA usando API de remisiones
   */
  const performOFIMAComparison = useCallback((sglOrders: SGLOrder[], ofimaOrders: EcommerceOrder[]): OrderComparison[] => {
    return sglOrders.map(sglOrder => {
      // Buscar el pedido correspondiente en OFIMA por ID
      const ofimaOrder = ofimaOrders.find(ofima => 
        compareIds(sglOrder.id.toString(), ofima.id)
      );
      
      // Realizar comparación
      const comparison = compareOrderDetails(sglOrder, ofimaOrder, ofimaOrder ? 'id' : 'none', 'OFIMA');
      
        return {
        ecommerceOrder: ofimaOrder || {
            id: '',
            orderNumber: '',
            status: '',
            total: 0,
            dateCreated: '',
            store: 'OFIMA'
          } as EcommerceOrder,
        sglOrder: sglOrder,
          comparison
        };
    });
  }, [compareIds, compareOrderDetails]);

  /**
   * Comparar pedidos con OFIMA usando API de remisiones
   */
  const compareOrdersWithOFIMA = useCallback(async (params: {
    store: string;
    page?: number;
    per_page?: number;
    dateRange?: { start: string; end: string };
    search?: string;
  }) => {
    // Obtener pedidos de SGL
    const sglResponse = await ComparativeService.getSGLOrders({
      stores: [params.store],
      page: params.page || 1,
      limit: params.per_page || 50,
      startDate: params.dateRange?.start,
      endDate: params.dateRange?.end,
      search: params.search,
    });

    const sglOrders = sglResponse.data;
    const sglPagination = sglResponse.meta; // Obtener información de paginación de SGL
    
    if (sglOrders.length === 0) {
      setComparisonData([]);
      setMetrics({
        totalEcommerceOrders: 0,
        totalSGLOrders: 0,
        synchronizedOrders: 0,
        missingInSGL: 0,
        discrepancies: 0,
        syncPercentage: 0
      });
      setPagination({
        currentPage: params.page || 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: params.per_page || 50
      });
      return { comparisons: [], metrics: null, pagination: null };
    }

    // Extraer IDs de SGL para enviar a la API de remisiones
    const sglIds = sglOrders.map(order => order.id.toString());
    
    // Obtener remisiones de OFIMA usando los IDs de SGL
    const ofimaResponse = await EcommerceService.getOFIMARemisiones(sglIds);
    
    let ofimaOrders: EcommerceOrder[] = [];
    if (ofimaResponse.success && ofimaResponse.data) {
      // Si es un array, usarlo directamente; si es un objeto, convertirlo a array
      ofimaOrders = Array.isArray(ofimaResponse.data) ? ofimaResponse.data : [ofimaResponse.data];
    }

    // Realizar comparación SGL → OFIMA
    const comparisons = performOFIMAComparison(sglOrders, ofimaOrders);
    
    setComparisonData(comparisons);

    // Calcular métricas
    const calculatedMetrics = calculateComparisonMetrics(comparisons, sglOrders.length, ofimaOrders.length);
    setMetrics(calculatedMetrics);

    // Actualizar paginación usando datos de SGL (que es la fuente de datos)
    const paginationData = {
      currentPage: sglPagination?.currentPage || params.page || 1,
      totalPages: sglPagination?.totalPages || 1,
      totalItems: sglPagination?.totalItems || sglOrders.length,
      pageSize: params.per_page || 50
    };
    
    setPagination(paginationData);

    return {
      comparisons,
      metrics: calculatedMetrics,
      pagination: {
        page: paginationData.currentPage,
        per_page: paginationData.pageSize,
        total: paginationData.totalItems,
        total_pages: paginationData.totalPages
      }
    };
  }, [performOFIMAComparison, calculateComparisonMetrics]);

  /**
   * Buscar pedido correspondiente (función genérica)
   */
  const findMatchingOrder = useCallback((sourceOrder: EcommerceOrder | SGLOrder, targetOrders: SGLOrder[] | EcommerceOrder[], store: string): { order: SGLOrder | EcommerceOrder | undefined; matchType: 'orders_id' | 'origin_order_id' | 'id' | 'total_date' | 'none' } => {
    if (store === 'OFIMA') {
      // SGL → OFIMA: buscar pedido de SGL en OFIMA
      return findSGLInOFIMA(sourceOrder as SGLOrder, targetOrders as EcommerceOrder[]);
    } else {
      // E-commerce → SGL: buscar pedido de e-commerce en SGL
      return findEcommerceInSGL(sourceOrder as EcommerceOrder, targetOrders as SGLOrder[], store);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Buscar pedido de SGL en OFIMA
   */
  const findSGLInOFIMA = useCallback((sglOrder: SGLOrder, ofimaOrders: EcommerceOrder[]): { order: EcommerceOrder | undefined; matchType: 'orders_id' | 'origin_order_id' | 'id' | 'total_date' | 'none' } => {
    // Para OFIMA: buscar en múltiples campos posibles con normalización de tipos
    // 1. id directo usando función auxiliar
    const idMatch = ofimaOrders.find(ofimaOrder => {
      const match = compareIds(sglOrder.id, ofimaOrder.id);
      return match;
    });
    if (idMatch) {
      return { order: idMatch, matchType: 'id' };
    }
    
    // 2. id_pedido_origen
    const idPedidoOrigenMatch = ofimaOrders.find(ofimaOrder => {
      const match = compareIds(sglOrder.id, ofimaOrder.id_pedido_origen);
      return match;
    });
    if (idPedidoOrigenMatch) {
      return { order: idPedidoOrigenMatch, matchType: 'origin_order_id' };
    }
    
    // 3. origin_order_id
    const originOrderIdMatch = ofimaOrders.find(ofimaOrder => {
      const match = compareIds(sglOrder.id, ofimaOrder.origin_order_id);
      return match;
    });
    if (originOrderIdMatch) {
      return { order: originOrderIdMatch, matchType: 'origin_order_id' };
    }
    
    // 4. number
    const numberMatch = ofimaOrders.find(ofimaOrder => {
      const match = compareIds(sglOrder.id, ofimaOrder.number);
      return match;
    });
    if (numberMatch) {
      return { order: numberMatch, matchType: 'orders_id' };
    }
    
    // 5. orders_id de SGL vs id de OFIMA
    const ordersIdMatch = ofimaOrders.find(ofimaOrder => {
      const match = compareIds(sglOrder.orders_id, ofimaOrder.id);
      return match;
    });
    if (ordersIdMatch) {
      return { order: ordersIdMatch, matchType: 'orders_id' };
    }
    
    return { order: undefined, matchType: 'none' };
  }, [compareIds]);

  /**
   * Buscar pedido de e-commerce en SGL
   */
  const findEcommerceInSGL = useCallback((ecommerceOrder: EcommerceOrder, sglOrders: SGLOrder[], store: string): { order: SGLOrder | undefined; matchType: 'orders_id' | 'origin_order_id' | 'id' | 'total_date' | 'none' } => {
    // Estrategias de búsqueda para e-commerces con normalización de tipos:
    // 1. Por orders_id (SGL) === orderNumber (e-commerce) - solo si orderNumber no está vacío
    if (ecommerceOrder.orderNumber && ecommerceOrder.orderNumber !== '-') {
      const ordersIdMatch = sglOrders.find(sglOrder => {
        const match = compareIds(sglOrder.orders_id, ecommerceOrder.orderNumber);
        return match;
      });
      if (ordersIdMatch) {
        return { order: ordersIdMatch, matchType: 'orders_id' };
      }
    }
    
    // 2. Por origin_order_id (SGL) === id (e-commerce)
    const originOrderIdMatch = sglOrders.find(sglOrder => {
      const match = compareIds(sglOrder.origin_order_id, ecommerceOrder.id);
      return match;
    });
    if (originOrderIdMatch) {
      return { order: originOrderIdMatch, matchType: 'origin_order_id' };
    }
    
    // 3. Por ID directo (SGL.id === ecommerce.id)
    const idDirectMatch = sglOrders.find(sglOrder => {
      const match = compareIds(sglOrder.id, ecommerceOrder.id);
      return match;
    });
    if (idDirectMatch) {
      return { order: idDirectMatch, matchType: 'id' };
    }
    
    // 4. Por orderNumber (e-commerce) === id (SGL) - solo si orderNumber no está vacío
    if (ecommerceOrder.orderNumber && ecommerceOrder.orderNumber !== '-') {
      const orderNumberMatch = sglOrders.find(sglOrder => {
        const match = compareIds(sglOrder.id, ecommerceOrder.orderNumber);
        return match;
      });
      if (orderNumberMatch) {
        return { order: orderNumberMatch, matchType: 'orders_id' };
      }
    }
    
    // 5. Estrategia especial para orderNumber vacío: buscar por ID en todos los campos de SGL
    if (!ecommerceOrder.orderNumber || ecommerceOrder.orderNumber === '-') {
      const exhaustiveMatch = sglOrders.find(sglOrder => {
        // Buscar en todos los campos posibles
        const matchInOrdersId = compareIds(sglOrder.orders_id, ecommerceOrder.id);
        const matchInOriginOrderId = compareIds(sglOrder.origin_order_id, ecommerceOrder.id);
        const matchInId = compareIds(sglOrder.id, ecommerceOrder.id);
        
        return matchInOrdersId || matchInOriginOrderId || matchInId;
      });
      
      if (exhaustiveMatch) {
        return { order: exhaustiveMatch, matchType: 'id' };
      }
    }
    
    // 6. Comparar por total (con tolerancia) - fallback
    const totalDateMatch = sglOrders.find(sglOrder => {
      const sglTotal = parseFloat(sglOrder.total);
      const ecommerceTotal = ecommerceOrder.total;
      const totalDifference = Math.abs(sglTotal - ecommerceTotal);
      const totalTolerance = sglTotal * 0.01; // 1% de tolerancia
      
      if (totalDifference <= totalTolerance) {
        // Comparar fechas (mismo día)
        const sglDate = new Date(sglOrder.date_created).toDateString();
        const ecommerceDate = new Date(ecommerceOrder.dateCreated).toDateString();
        
        if (sglDate === ecommerceDate) {
          return true;
        }
      }
      
      return false;
    });
    
    if (totalDateMatch) {
      return { order: totalDateMatch, matchType: 'total_date' };
    }
    
    return { order: undefined, matchType: 'none' };
  }, [compareIds]);

  /**
   * Realizar comparación detallada entre pedidos
   */
  const performOrderComparison = useCallback((sourceOrders: EcommerceOrder[] | SGLOrder[], targetOrders: SGLOrder[] | EcommerceOrder[], store: string): OrderComparison[] => {
    return sourceOrders.map(sourceOrder => {
      // Para OFIMA: sourceOrder es SGL, targetOrder es OFIMA
      // Para e-commerces: sourceOrder es e-commerce, targetOrder es SGL
      const { order: targetOrder, matchType } = findMatchingOrder(sourceOrder, targetOrders, store);
      
      // Realizar comparación
      const comparison = compareOrderDetails(sourceOrder, targetOrder, matchType, store);
      
      // Construir el resultado según la dirección
      if (store === 'OFIMA') {
        // SGL → OFIMA
        return {
          ecommerceOrder: targetOrder as EcommerceOrder || {
            id: '',
            orderNumber: '',
            status: '',
            total: 0,
            dateCreated: '',
            store: 'OFIMA'
          } as EcommerceOrder,
          sglOrder: sourceOrder as SGLOrder,
          comparison
        };
      } else {
        // E-commerce → SGL
      return {
          ecommerceOrder: sourceOrder as EcommerceOrder,
          sglOrder: targetOrder as SGLOrder || {
            id: 0,
            orders_id: '',
            status: '',
            total: '0',
            date_created: '',
            store_name: ''
          } as SGLOrder,
          comparison
        };
      }
    });
  }, [findMatchingOrder, compareOrderDetails]);

  /**
   * Comparar pedidos con e-commerces (lógica tradicional)
   */
  const compareOrdersWithEcommerce = useCallback(async (params: {
    store: string;
    page?: number;
    per_page?: number;
    dateRange?: { start: string; end: string };
    search?: string;
  }) => {
    // Obtener pedidos de ambos sistemas en paralelo
    const [sglResponse, ecommerceResponse] = await Promise.all([
      ComparativeService.getSGLOrders({
        stores: [params.store],
        page: params.page || 1,
        limit: params.per_page || 50,
        startDate: params.dateRange?.start,
        endDate: params.dateRange?.end,
        search: params.search,
      }),
      EcommerceService.getOrders({
        store: params.store,
        page: params.page || 1,
        per_page: params.per_page || 50,
        date_created_after: params.dateRange?.start,
        date_created_before: params.dateRange?.end,
        search: params.search,
      })
    ]);

    const sglOrders = sglResponse.data;
    const ecommerceOrders = ecommerceResponse.data.orders;

    // Realizar comparación E-commerce → SGL
    const comparisons = performOrderComparison(ecommerceOrders, sglOrders, params.store);
    
    setComparisonData(comparisons);

    // Calcular métricas
    const calculatedMetrics = calculateComparisonMetrics(comparisons, sglOrders.length, ecommerceOrders.length);
    setMetrics(calculatedMetrics);

    // Actualizar paginación
    const paginationData = ecommerceResponse.data.pagination;
    setPagination({
      currentPage: paginationData.page || params.page || 1,
      totalPages: paginationData.total_pages || 1,
      totalItems: paginationData.total || 0,
      pageSize: params.per_page || 50
    });

    return {
      comparisons,
      metrics: calculatedMetrics,
      pagination: paginationData
    };
  }, [performOrderComparison, calculateComparisonMetrics]);

  /**
   * Comparar pedidos entre SGL y Ecommerce
   * 
   * Para OFIMA: Usar API de remisiones con IDs de SGL
   * Para e-commerces: Estrategias de matching tradicionales
   */
  const compareOrders = useCallback(async (params: {
    store: string;
    page?: number;
    per_page?: number;
    dateRange?: { start: string; end: string };
    search?: string;
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      if (params.store === 'OFIMA') {
        // Lógica especial para OFIMA: usar API de remisiones
        return await compareOrdersWithOFIMA(params);
      } else {
        // Lógica tradicional para e-commerces
        return await compareOrdersWithEcommerce(params);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en la comparación';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [compareOrdersWithOFIMA, compareOrdersWithEcommerce]);

  /**
   * Filtrar comparaciones por estado de sincronización
   */
  const filterBySyncStatus = useCallback((status: 'complete' | 'missing' | 'discrepancy') => {
    return comparisonData.filter(comparison => comparison.comparison.syncStatus === status);
  }, [comparisonData]);

  /**
   * Obtener resumen de discrepancias
   */
  const getDiscrepancySummary = useMemo(() => {
    const summary: { [key: string]: number } = {};
    
    comparisonData.forEach(comparison => {
      comparison.comparison.discrepancies.forEach(discrepancy => {
        const type = discrepancy.split(':')[0];
        summary[type] = (summary[type] || 0) + 1;
      });
    });
    
    return summary;
  }, [comparisonData]);

  return {
    isLoading,
    error,
    comparisonData,
    metrics,
    pagination,
    compareOrders,
    filterBySyncStatus,
    getDiscrepancySummary,
    clearData: () => {
      setComparisonData([]);
      setMetrics(null);
      setError(null);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 50
      });
    }
  };
};